import React from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gl26_auth_token";
const AuthContext = React.createContext(null);

// Make every request include cookies so the access_token httpOnly cookie is sent
axios.defaults.withCredentials = true;

// Bearer-token fallback when cookies are blocked by the browser (e.g. Brave strict mode)
const installAuthHeaderInterceptor = () => {
  axios.interceptors.request.use((config) => {
    const url = config.url || "";
    if (url.includes("/api/") && !url.includes("/api/auth/login")) {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${t}`;
        }
      }
    }
    return config;
  });
};
installAuthHeaderInterceptor();

export const AuthProvider = ({ children }) => {
  // null = checking, false = anonymous, {username} = authenticated
  const [user, setUser] = React.useState(null);

  const checkSession = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  React.useEffect(() => { checkSession(); }, [checkSession]);

  // Intercept 401 globally — kick the user back to login
  React.useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setUser(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { username, password });
    if (data?.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    setUser({ username: data.username });
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
